import Card, { CardHeader } from '../../components/ui/Card';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communicationsApi } from '../../api/communications';
import LetterheadForm from '../../components/documents/LetterheadForm';
import type { Letterhead } from '../../types';

export default function CityLetterheadsTab() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showCreator, setShowCreator] = useState(false);

  const { data: letterheads = [], isLoading } = useQuery({
    queryKey: ['letterheads'],
    queryFn: () => communicationsApi.getLetterheads(),
  });

  const cityDefaults = letterheads.filter((lh) => lh.is_city_default);
  const personal = letterheads.filter((lh) => !lh.is_city_default);

  const createMut = useMutation({
    mutationFn: communicationsApi.createLetterhead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['letterheads'] });
      setShowCreator(false);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Letterhead> }) =>
      communicationsApi.updateLetterhead(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['letterheads'] });
      setEditingId(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => communicationsApi.deleteLetterhead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['letterheads'] }),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex items-center justify-between">
          <h3 className="text-lg font-medium">
            City-Standard-Briefköpfe
            <span className="ml-2 text-sm font-normal text-gray-500">({cityDefaults.length})</span>
          </h3>
          {!showCreator && (
            <button
              onClick={() => setShowCreator(true)}
              className="btn btn-text text-sm"
            >
              + Neuer Briefkopf
            </button>
          )}
        </CardHeader>
        {showCreator && (
          <div className="px-6 py-4 border-b bg-gray-50">
            <LetterheadForm
              allowCityDefaultToggle
              defaultIsCityDefault
              onSubmit={(body) => createMut.mutateAsync(body)}
              onCancel={() => setShowCreator(false)}
              isSubmitting={createMut.isPending}
              submitLabel="Briefkopf anlegen"
            />
          </div>
        )}

        {isLoading ? (
          <div className="px-6 py-6 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : cityDefaults.length === 0 ? (
          <p className="px-6 py-4 text-sm text-gray-500">
            Noch keine City-Standard-Briefköpfe angelegt. Sachbearbeiter sehen nur eigene Briefköpfe,
            bis hier mindestens einer hinterlegt ist.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {cityDefaults.map((lh) => (
              <LetterheadRow
                key={lh.id}
                lh={lh}
                isEditing={editingId === lh.id}
                onEdit={() => setEditingId(lh.id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={(body) => updateMut.mutateAsync({ id: lh.id, body })}
                onDelete={() => {
                  if (confirm(`Briefkopf "${lh.name}" wirklich löschen?`)) {
                    deleteMut.mutate(lh.id);
                  }
                }}
                isSaving={updateMut.isPending}
              />
            ))}
          </ul>
        )}
      </Card>

      {personal.length > 0 && (
        <Card>
          <CardHeader title={<>
              Eigene Briefköpfe von Sachbearbeitern
              <span className="ml-2 text-sm font-normal text-gray-500">({personal.length})</span>
            </>} />
          <ul className="divide-y divide-gray-200">
            {personal.map((lh) => (
              <li key={lh.id} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{lh.name}</p>
                  <p className="text-xs text-gray-500">
                    {lh.owner_name ?? `User #${lh.owner_user_id ?? '?'}`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Briefkopf "${lh.name}" wirklich löschen?`)) {
                      deleteMut.mutate(lh.id);
                    }
                  }}
                  className="btn btn-text text-danger-700 enabled:hover:text-danger-900 focus-visible:ring-danger-500 text-xs"
                >
                  Löschen
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

interface RowProps {
  lh: Letterhead;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (body: Partial<Letterhead>) => Promise<unknown>;
  onDelete: () => void;
  isSaving: boolean;
}

function LetterheadRow({ lh, isEditing, onEdit, onCancelEdit, onSave, onDelete, isSaving }: RowProps) {
  if (isEditing) {
    return (
      <li className="px-6 py-4 bg-gray-50">
        <LetterheadForm
          initial={lh}
          allowCityDefaultToggle
          onSubmit={(body) => onSave(body)}
          onCancel={onCancelEdit}
          isSubmitting={isSaving}
          submitLabel="Änderungen speichern"
        />
      </li>
    );
  }
  return (
    <li className="px-6 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">{lh.name}</p>
          <pre className="text-xs text-gray-600 mt-1 whitespace-pre-wrap font-sans">
            {lh.sender_block}
          </pre>
          {lh.contact_line && (
            <p className="text-xs text-gray-500 mt-1">{lh.contact_line}</p>
          )}
          {lh.signature_block && (
            <p className="text-xs text-gray-500 mt-1 italic">{lh.signature_block}</p>
          )}
        </div>
        <div className="flex gap-3 flex-shrink-0">
          <button onClick={onEdit} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-xs">
            Bearbeiten
          </button>
          <button onClick={onDelete} className="btn btn-text text-danger-700 enabled:hover:text-danger-900 focus-visible:ring-danger-500 text-xs">
            Löschen
          </button>
        </div>
      </div>
    </li>
  );
}
